import { useState, useCallback } from 'react'

export interface ErrorState {
  message: string
  type: 'error' | 'warning' | 'info'
  timestamp: number
  retry?: () => void
  dismiss?: () => void
}

export interface UseErrorHandlerReturn {
  errors: ErrorState[]
  addError: (message: string, type?: ErrorState['type'], retry?: () => void) => void
  addWarning: (message: string, retry?: () => void) => void
  addInfo: (message: string) => void
  clearError: (index: number) => void
  clearAllErrors: () => void
  hasErrors: boolean
  hasWarnings: boolean
}

export function useErrorHandler(): UseErrorHandlerReturn {
  const [errors, setErrors] = useState<ErrorState[]>([])

  const addError = useCallback((
    message: string,
    type: ErrorState['type'] = 'error',
    retry?: () => void
  ) => {
    const newError: ErrorState = {
      message,
      type,
      timestamp: Date.now(),
      retry,
      dismiss: () => {
        setErrors(prev => prev.filter(e => e.timestamp !== newError.timestamp))
      }
    }

    setErrors(prev => [...prev, newError])

    // Auto-dismiss info messages after 5 seconds
    if (type === 'info') {
      setTimeout(() => {
        setErrors(prev => prev.filter(e => e.timestamp !== newError.timestamp))
      }, 5000)
    }

    // Auto-dismiss warnings after 10 seconds
    if (type === 'warning') {
      setTimeout(() => {
        setErrors(prev => prev.filter(e => e.timestamp !== newError.timestamp))
      }, 10000)
    }
  }, [])

  const addWarning = useCallback((message: string, retry?: () => void) => {
    addError(message, 'warning', retry)
  }, [addError])

  const addInfo = useCallback((message: string) => {
    addError(message, 'info')
  }, [addError])

  const clearError = useCallback((index: number) => {
    setErrors(prev => prev.filter((_, i) => i !== index))
  }, [])

  const clearAllErrors = useCallback(() => {
    setErrors([])
  }, [])

  const hasErrors = errors.some(error => error.type === 'error')
  const hasWarnings = errors.some(error => error.type === 'warning')

  return {
    errors,
    addError,
    addWarning,
    addInfo,
    clearError,
    clearAllErrors,
    hasErrors,
    hasWarnings
  }
}
